import { onboardingSidebar, onboardingConstants } from '@/lib/constants';
import { createReducer } from '@/redux/root';
import { DOCUMENT_TYPES } from '@/lib/constants/approval';

import { logoutAction } from '../auth';

import {
  nameSpace,
  setSelectedAction,
  setSelectedPropAction,
  arrayAddItemAction,
  arrayRemoveItemAction,
  arrayChangeFieldAction,
  setCurrentStepAction,
  setNextStepAction,
  resetSelectedAction,
  resetStepsAction,
  requestRepEditHistoryAsync,
  setOnboardingDataUpdated,
  setOnboardingFormCompleted,
  resetProfileCompletion,
  setFeatureFlagAction,
  requestAdminViewAsync,
  clearRepProfileStateAction,
  requestSalesRanksAsync,
  requestPronounsAsync,
  requestTimezonesAsync,
} from './actions';

import {
  uploadProfilePicAsync,
  uploadPassportAsync,
  uploadDriverLicenseAsync,
  uploadSsCardAsync,
} from '../files/actions';

const {
  DIRECT_DEPOSIT_BANK_NAME,
  DIRECT_DEPOSIT_ACCOUNT_NAME,
  DIRECT_DEPOSIT_ACCOUNT_TYPE,
  DIRECT_DEPOSIT_ROUTING_NUMBER,
  DIRECT_DEPOSIT_ACCOUNT_NUMBER,
  DIRECT_DEPOSIT_CONFIRM_ACCOUNT_NUMBER,
  PENDING_APPROVAL_ITEMS_NAME,
} = onboardingConstants;

const initialState = {
  currentStep: onboardingSidebar.PERSONAL_INFO_STEP_ID,
  nextStep: null,
  onboardingCompletion : {},
  salesRanks: [],
  pronouns: [],
  timezones: [],
  selected: {
    userId: null,
    onboarded: false,
    approved: false,
    signature: '',
    dob: '',
    repAcknowledgment: false,
    profilePictureLocalFile: '',
    passportPictureLocalFile: '',
    socialSecurityPictureLocalFile: '',
    driverLicenseLocalFile: '',
    signatureLocalFile: '',
    profilePicUrl: '',
    socialSecurityUrl: '',
    driverLicenseUrl: '',
    passportUrl: '',
    signatureUrl: '',
    usesType: '',
    wotcSurveyCompleted: false,
    w9Clicked: false,
    i9Clicked: false,
    workdayComplete: false,
    [DIRECT_DEPOSIT_BANK_NAME]: '',
    [DIRECT_DEPOSIT_ACCOUNT_NAME]: '',
    [DIRECT_DEPOSIT_ACCOUNT_TYPE]: '',
    [DIRECT_DEPOSIT_ROUTING_NUMBER]: '',
    [DIRECT_DEPOSIT_ACCOUNT_NUMBER]: '',
    [DIRECT_DEPOSIT_CONFIRM_ACCOUNT_NUMBER]: '',

    [PENDING_APPROVAL_ITEMS_NAME]: [],

    // Private, immutable data: should not be shown to the user or modified
    _: {
      [DIRECT_DEPOSIT_ACCOUNT_NUMBER]: '',
    },
  },
  editHistory: {
    items: [],
    total: 0,
  },
  featureFlags: {},
};

export const onboardingReducer = createReducer(nameSpace, initialState, {
  [uploadProfilePicAsync.success]: ({ state }) => {
    state.selected[PENDING_APPROVAL_ITEMS_NAME] = [
      ...state.selected.pendingApprovalItems,
      DOCUMENT_TYPES.PROFILE_IMG,
    ];
  },

  [uploadPassportAsync.success]: ({ state }) => {
    state.selected[PENDING_APPROVAL_ITEMS_NAME] = [
      ...state.selected.pendingApprovalItems,
      DOCUMENT_TYPES.PASSPORT_IMG,
    ];
  },

  [uploadDriverLicenseAsync.success]: ({ state }) => {
    state.selected[PENDING_APPROVAL_ITEMS_NAME] = [
      ...state.selected.pendingApprovalItems,
      DOCUMENT_TYPES.DL_IMG,
    ];
  },

  [uploadSsCardAsync.success]: ({ state }) => {
    state.selected[PENDING_APPROVAL_ITEMS_NAME] = [
      ...state.selected.pendingApprovalItems,
      DOCUMENT_TYPES.SS_IMG,
    ];
  },

  [arrayAddItemAction]: ({ state, action }) => {
    const { parent, listItem } = action.payload;

    state.selected[parent] = [
      ...state.selected[parent],
      listItem,
    ];
  },

  [arrayChangeFieldAction]: ({ state, action }) => {
    const { parent, index, name, value } = action.payload;

    state.selected[parent][index] = {
      ...state.selected[parent][index],
      [name]: value,
    };
  },

  [arrayRemoveItemAction]: ({ state, action }) => {
    const { parent, id } = action.payload;

    state.selected[parent] = state.selected[parent].filter((el) => el.setId !== id);
  },

  [setSelectedAction]: ({ state, action }) => {
    state.selected = {
      ...state.selected,
      ...action.payload,
    };
  },

  [setOnboardingDataUpdated]: ({ state, action }) => {
    state.selected.isUpdated = action.payload;
  },

  [setSelectedPropAction]: ({ state, action }) => {
    const { name, value } = action.payload;

    state.selected[name] = value;
  },

  [resetSelectedAction]: ({ state }) => {
    state.selected = initialState.selected;
  },

  [setCurrentStepAction]: ({ state, action }) => {
    state.currentStep = action.payload;
  },

  [setNextStepAction]: ({ state, action }) => {
    state.nextStep = action.payload;
  },

  [resetStepsAction]: ({ state }) => {
    state.currentStep = initialState.currentStep;
    state.nextStep = initialState.nextStep;
  },

  [logoutAction]: ({ state }) => {
    state.selected = initialState.selected;
    state.currentStep = initialState.currentStep;
    state.nextStep = initialState.nextStep;
  },

  [requestRepEditHistoryAsync.success]: ({ state, action }) => {
    state.editHistory = action.payload.toPlainObject();
  },

  [requestRepEditHistoryAsync.failure]: ({ state, action }) => {
    state.editHistory = action.payload.toPlainObject();
  },

  [setOnboardingFormCompleted]: ({ state, action }) => {
    const { formId, isCompleted } = action.payload;

    state.onboardingCompletion[formId] = isCompleted;
  },

  [resetProfileCompletion]: ({ state }) => {
    state.onboardingCompletion = {};
  },

  [setFeatureFlagAction]: ({ state, action }) => {
    state.featureFlags = {
      ...state.featureFlags,
      [action.payload.name]: action.payload.value,
    };
  },

  [requestAdminViewAsync.success]: ({ state, action: { payload } }) => {
    const { data } = payload;

    state.adminView = data?.attributes;
  },

  [clearRepProfileStateAction]: ({ state }) => {
    state.selected = initialState.selected;
  },

  [requestSalesRanksAsync.success]: ({ state, action: { payload } }) => {
    const { data } = payload;

    state.salesRanks = data.map((salesRankData) => salesRankData?.attributes);
  },

  [requestPronounsAsync.success]: ({ state, action: { payload } }) => {
    const { data } = payload;

    state.pronouns = data.map((pronounData) => pronounData?.attributes);
  },

  [requestTimezonesAsync.success]: ({ state, action: { payload } }) => {
    const { data } = payload;

    state.timezones = data.map((timezonesData) => timezonesData?.attributes);
  },
});
